import StateObserver, { user } from 'src/StateObserver';
import { GCInstant } from '../../lib/gcinstant';

import { NativeLink, NativeNotification } from '@play-co/gcinstant';
import { captureGenericError } from '../sentry';
import { EntryProperties } from '../analytics/properties';
import { hideLoading, showLoading } from 'src/state/ui';
import { canDisplayNativeLogin, nativeRestart } from './native.login';

export const isPlatformNative = () =>
  GCInstant.osType === 'ANDROID_APP' || GCInstant.osType === 'IOS_APP';

/**
 * Native logout function that handles the logout process for both legacy and new native apps.
 * Works for legacy native app apps. For native app iOS <=2.11.0 and all Android versions,
 * And new native app iOS >=2.12.0.
 */
export const logoutFromNative = async () => {
  // Should we use new native login module or legacy native app logout
  if (canDisplayNativeLogin()) {
    await nativeRestart(true);
  } else {
    await GCInstant.legacyNative?.auth.logout();
  }
};

let reloading = false;

/**
 * Force game update and reentry function that handles the game reload process.
 * Works for legacy native app apps. For native app iOS <=2.11.0 and all Android versions,
 * And new native app iOS >=2.12.0.
 */
export const forceGameUpdateAndReentry = async (requireLogin = false) => {
  // Prevent multiple reloads to avoid unpredictable behavior.
  if (reloading) return;
  reloading = true;

  // Need to show loading screen to prevent the user from interacting with the app.
  StateObserver.dispatch(showLoading());

  try {
    // flush and pause replicant to prevent any further state changes and/or errors.
    await StateObserver.flush();
    StateObserver.replicant?.pause();

    // New native logout flow and legacy native app logout
    if (canDisplayNativeLogin()) {
      await nativeRestart(requireLogin);
    } else {
      // Legacy native app needs to be restarted.
      await (window as any).TLNative?.core?.restart?.(requireLogin);

      // Legacy native app logout
      if (requireLogin) {
        await logoutFromNative();
      }
    }

    // Fallback to reload the page if the native restart didn't work.
    window.location.reload();
  } finally {
    reloading = false;
    StateObserver.dispatch(hideLoading());
  }
};

export const requestReview = () => {
  GCInstant.legacyNative?.store.requestReview({});
};

export const openingLink = (async () => {
  const link = await GCInstant.legacyNative?.links.firstOpened();
  if (link) {
    // Remove the link from the queue, to prevent it from being handled twice
    await link.remove?.();
  }

  return link?.url;
})();

export const subscribeToLinks = (callback: (link: string) => void) => {
  GCInstant.legacyNative?.links.subscribe((link: NativeLink) => {
    callback(link.url);
    link.remove?.();
  });
};

export const openingNotification = (async () => {
  const notification =
    await GCInstant.legacyNative?.notifications.firstOpened();
  if (notification) {
    // Remove the notification from the queue, to prevent it from being handled twice
    await notification.remove?.();
  }
  return notification;
})();

export const subscribeToNotifications = (
  callback: (notification: NativeNotification) => void,
) => {
  GCInstant.legacyNative?.notifications.subscribe(
    (notification: NativeNotification) => {
      callback(notification);
      notification.remove?.();
    },
  );
};

let loadedFromSavedPayload = false;
export function consumeSavedPayload() {
  const entryData: Partial<EntryProperties> = {};

  // merge the cached native payload if it exists
  const payload = user().native.payload ?? null;
  if (payload) {
    loadedFromSavedPayload = true;
    void StateObserver.invoke.setPersistedPayload(undefined);

    try {
      Object.assign(entryData, JSON.parse(payload));
    } catch (err) {
      captureGenericError(
        `Error augmenting entry data with saved native payload`,
        err,
      );
    }
  }

  return entryData;
}

export function wasLoadedFromSavedPayload() {
  return loadedFromSavedPayload;
}
